import { useState } from "react";
import { GraduationCap, Play, RotateCcw } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { authApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";

/**
 * OnboardingSettingsCard - Card component for settings page
 * Allows users to restart the onboarding flow
 */
export const OnboardingSettingsCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const updateUser = useAuthStore((state) => state.updateUser);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const handleRestartOnboarding = async () => {
    setIsResetting(true);
    try {
      await authApi.resetOnboarding();
      updateUser({ hasCompletedOnboarding: false });
      setShowConfirmDialog(false);
      toast.success(
        t("settings.onboarding.resetSuccess", "Starting onboarding..."),
      );
      navigate("/onboarding");
    } catch (error) {
      console.error("Failed to reset onboarding:", error);
      toast.error(
        t("settings.onboarding.resetError", "Failed to restart onboarding"),
      );
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <>
      {/* Settings Card */}
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <GraduationCap className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-foreground mb-1">
              {t("settings.onboarding.title", "Getting Started Guide")}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t(
                "settings.onboarding.description",
                "Review the onboarding tutorial to learn about eScribe's features and how to get the most out of the platform.",
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowConfirmDialog(true)}
              className="gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              {t("settings.onboarding.restart", "Restart Onboarding")}
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-primary" />
              {t("settings.onboarding.restartTitle", "Restart Onboarding?")}
            </DialogTitle>
            <DialogDescription>
              {t(
                "settings.onboarding.restartDescription",
                "This will take you through the onboarding tutorial again. You can skip it at any time.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isResetting}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleRestartOnboarding}
              disabled={isResetting}
              className="gap-2"
            >
              {isResetting ? (
                t("common.loading", "Loading...")
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  {t("settings.onboarding.startTour", "Start Tour")}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * OnboardingSettingsItem - Menu item for settings navigation
 */
export const OnboardingSettingsItem = ({ onClick }) => {
  const { t } = useTranslation();

  return (
    <button
      onClick={onClick}
      className="w-full p-4 rounded-xl border bg-card hover:bg-accent/50 transition-colors text-left flex items-start gap-4"
    >
      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
        <GraduationCap className="w-6 h-6 text-primary" />
      </div>
      <div>
        <h3 className="font-semibold mb-1">
          {t("settings.onboarding.menuItem", "Getting Started")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t(
            "settings.onboarding.menuDescription",
            "Review the onboarding tutorial",
          )}
        </p>
      </div>
    </button>
  );
};

export default OnboardingSettingsCard;
